/**
 * Autoprint configuration hooks for HMH Lunch Ordering System.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiConfig } from "../config/msalConfig";

export interface AutoprintSettings {
  enabled: boolean;
  print_time: string;
  printer_name: string | null;
  cups_host: string;
  last_print_date: string | null;
  last_print_at: string | null;
  last_print_status: "success" | "failed" | null;
  last_print_detail: string | null;
}

export interface Printer {
  name: string;
  state: string;
  state_message: string;
  info: string;
  location: string;
}

export interface UpdateAutoprintSettingsRequest {
  enabled?: boolean;
  print_time?: string;
  printer_name?: string;
  cups_host?: string;
}

interface TestPrintResponse {
  status: string;
  job_id: number;
  printer: string;
  order_date: string;
}

export function useAutoprintSettings() {
  return useQuery<AutoprintSettings>({
    queryKey: ["autoprint-settings"],
    queryFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${apiConfig.baseUrl}/admin/autoprint/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch autoprint settings");
      }

      return response.json();
    },
  });
}

export function useUpdateAutoprintSettings() {
  const queryClient = useQueryClient();

  return useMutation<AutoprintSettings, Error, UpdateAutoprintSettingsRequest>({
    mutationFn: async (data) => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${apiConfig.baseUrl}/admin/autoprint/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to update autoprint settings");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["autoprint-settings"] });
    },
  });
}

export function usePrinters() {
  return useQuery<{ printers: Printer[] }>({
    queryKey: ["autoprint-printers"],
    queryFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${apiConfig.baseUrl}/admin/autoprint/printers`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to reach the CUPS print server");
      }

      return response.json();
    },
    retry: false,
  });
}

export function useTestPrint() {
  const queryClient = useQueryClient();

  return useMutation<TestPrintResponse, Error, void>({
    mutationFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${apiConfig.baseUrl}/admin/autoprint/test-print`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Test print failed");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["autoprint-settings"] });
    },
  });
}
